import { SVGIconProps } from "@faire/slate-icons/types";
import { componentCls } from "@faire/slate/lib/tracking";
import { tsv } from "@faire/slate/lib/tsv";
import { FilterButtonBase } from "@faire/slate/private/filter/FilterBase";
import {
  IFilterButtonWebProps,
  FilterButtonVariant,
} from "@faire/slate/private/filter/types";
import React from "react";

type IProps = {
  /**
   * The filter button variant.
   *
   * @default "rest"
   */
  variant?: FilterButtonVariant;

  /**
   * The filter icon.
   */
  Icon?: React.FC<SVGIconProps>;

  /**
   * The filter label.
   */
  label: string;

  /**
   * Whether the filter is disabled.
   *
   * @default false
   */
  disabled?: boolean;

  // DO NOT ADD any new prop without aligning with Slate team first
} & IFilterButtonWebProps;

/**
 * Filters components help users view a subset of content.
 *
 * Filter Button is intended to be used as a button that performs an action when clicked.
 *
 * @remarks
 * Differently from Filter Toggle, it shouldn't change state when clicked. This is a very important
 * distinction because it changes the way the component is implemented from an accessibility standpoint.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 * 
 * {@link https://www.notion.so/faire/Filter-2242efb5c25a80b8adb5c588806269bf | Slate/FilterButton}
 */
export const FilterButton: React.FC<IProps> = ({
  variant = "rest",
  ...props
}) => {
  const filterButtonCls = filterButtonStyle.cls({
    variant,
    className: componentCls(),
  });

  return (
    <FilterButtonBase
      {...props}
      className={filterButtonCls}
      data-component-name="Slate/FilterButton"
    />
  );
};

const filterButtonStyle = tsv({
  variants: {
    variant: {
      rest: null,
      applied: "fs-filter--applied",
    },
  },
});
